import React, { useEffect, useState } from 'react'
import {
  MotifCard,
  MotifCardHeader,
  MotifCardBody,
  MotifButton,
  MotifIcon,
  MotifTooltip,
  MotifFormField,
  MotifToggleSwitch,
  MotifProgressLoader,
  MotifSearch,
} from '@ey-xd/motif-react'
import { IconoirPlus } from '@ey-xd/motif-icon'
import { useListApplicationQuery } from '@/services/application/applicationReducer'
import './ApplicationList.scss'
import AddApplicationModal from './AddApplicationModal'
import { useParams } from 'react-router-dom'
import Table from '@/components/Table/table'
import { useColumnApplicationListDefs } from './Constants'
import { useSelector } from 'react-redux'
import { EngagementDetail } from '@/services/types'

export default function ApplicationList() {
  const [showModal, setShowModal] = useState(false)
  const [searchInput, setSearchInput] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const { engagementId } = useParams<{ engagementId: string }>()
  const [editApplication, setEditApplication] = useState(null)
  const currentEngagement = useSelector(
    (state: { clientCard: { currentEngagement: EngagementDetail } }) =>
      state.clientCard.currentEngagement,
  )
  const isArchived = currentEngagement?.isArchived
  // engagement not loaded yet (e.g. direct navigation) - isArchived would be undefined, so gate on load too
  const isEngagementLoaded = !!currentEngagement?.engagementID

  // Debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchTerm(searchInput)
    }, 400) // 400ms debounce

    return () => clearTimeout(handler)
  }, [searchInput])

  const {
    data: applicationList,
    isLoading,
    refetch,
  } = useListApplicationQuery({
    engagementId: engagementId || '',
    query: searchTerm ? searchTerm : '',
  })
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(event.target.value.trim())
  }

  const handleModalClose = (closeState: string) => {
    setShowModal(false)
    if (closeState === 'create') {
      refetch()
    }
  }

  const handleClearSearch = () => setSearchInput('')


  return (
    <>
      <div className="motif-row">
        <div className="motif-col-lg-12">
          <MotifCard
            className="application-list-container"
            orientation="horizontal"
            variant="full-width"
          >
            <MotifCardHeader>
              <div className="motif-container">
                <div className="motif-row">
                  <div className="motif-col-lg-6">
                    <span className="motif-h6-default-regular">
                      Information Technologies List
                    </span>
                    <div className="motif-card-subheader">
                      <span className="motif-body2-default-regular sub-header">
                        Identify or create the relevant technologies (i.e., applications or tools) you wish to review for the Segregation of Duties (SoD) analysis.
                      </span>
                    </div>
                  </div>
                  <div className="motif-col-sm-4 motif-col-md-4 motif-col-lg-4">
                    <MotifFormField>
                      <MotifSearch
                        aria-label="Search"
                        id="client-search"
                        items={[]}
                        onChange={handleSearchChange} // Handle search input
                        placeholder="Search"
                        onClear={handleClearSearch}
                      />
                    </MotifFormField>
                  </div>
                  <div className="motif-col-lg-2 upload-button">
                    {isEngagementLoaded && !isArchived && (
                      <MotifTooltip
                        hideCloseButton
                        flip
                        forceHideCounter={5}
                        id="tooltip"
                        offset={[0, 8]}
                        placement="left"
                        style={{ marginLeft: '10px' }}
                        trigger={
                          <MotifButton
                            size="small"
                            type="button"
                            variant="primary"
                            onClick={() => {
                              setEditApplication(null)
                              setShowModal(true)
                            }}
                          >
                            <MotifIcon
                              iconFunction={IconoirPlus}
                              title="Upload HR document"
                            />
                            Add application
                          </MotifButton>
                        }
                        usePortal
                        variant="default"
                      >
                        <div className="motif-body2-default-bold">
                          Add application
                        </div>
                        <div className="motif-caption-default-light motif-tooltip-custom-width">
                          Add relevant applications for your SOD analysis. This
                          includes adding name and description of each
                          application.
                        </div>
                      </MotifTooltip>
                    )}
                  </div>
                </div>
              </div>
            </MotifCardHeader>
            <MotifCardBody>
              {isLoading ? (
                <MotifProgressLoader />
              ) : (
                <Table
                  key={searchTerm || 'full-list'}
                  columnDefs={useColumnApplicationListDefs({
                    refetch,
                    onEdit: (data: any) => {
                      setEditApplication(data)
                      setShowModal(true)
                    },
                  })}
                  cellRenderers={applicationList || []}
                  TitleComplement={() => (
                    <MotifFormField>
                      <MotifToggleSwitch
                        id="defaultUnitToggle"
                        onLabel=""
                        offLabel="Default Unit"
                        checked={true}
                        onChange={() => { }}
                      />
                    </MotifFormField>
                  )}
                  title="title"
                  description="description"
                  pagination={true}
                />
              )}
            </MotifCardBody>
          </MotifCard>
        </div>
      </div>
      <AddApplicationModal
        isVisible={showModal}
        setIsVisible={handleModalClose}
        application={editApplication ?? null}
        editApp={editApplication}
      />
    </>
  )
}
